import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, WifiOff } from "lucide-react";
import { CameraPreview } from "@/components/timer/CameraPreview";
import {
	CameraTransport,
	type LaneRegion,
} from "@/lib/timer/camera-transport";
import { RelayQueue } from "@/lib/relay-queue";
import type { AppCommand, DeviceMessage, TimerState } from "@/lib/timer/types";

const POLL_INTERVAL_MS = 200;
const HEARTBEAT_INTERVAL_MS = 3_000;

export const Route = createFileRoute("/sensor")({
	validateSearch: (search: Record<string, unknown>) => ({
		s: (search.s as string) || "",
	}),
	component: SensorPage,
});

function SensorPage() {
	const { s: initialCode } = useSearch({ from: "/sensor" });
	const [sessionCode, setSessionCode] = useState(initialCode);
	const [inputCode, setInputCode] = useState(initialCode);
	const [connected, setConnected] = useState(false);
	const [cameraReady, setCameraReady] = useState(false);
	const [timerState, setTimerState] = useState<TimerState>("idle");
	const [error, setError] = useState<string | null>(null);
	const [calibrating, setCalibrating] = useState(false);
	const [sensitivity, setSensitivity] = useState(0.3);
	const [regions, setRegions] = useState<LaneRegion[]>([]);
	const [stream, setStream] = useState<MediaStream | null>(null);
	const [pendingCount, setPendingCount] = useState(0);
	const [laneCount, setLaneCount] = useState(4);
	const [laneResults, setLaneResults] = useState(
		new Map<
			number,
			{
				lane: number;
				elapsed_ms: number | null;
				status: "pending" | "valid" | "dnf";
			}
		>(),
	);

	const transportRef = useRef<CameraTransport | null>(null);
	const lastSeenIdRef = useRef(0);
	const pollingRef = useRef(false);
	const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(
		null,
	);
	const queueRef = useRef<RelayQueue>(new RelayQueue());

	// Use refs for values accessed in setInterval callbacks
	const sessionCodeRef = useRef(sessionCode);
	useEffect(() => {
		sessionCodeRef.current = sessionCode;
	}, [sessionCode]);
	const laneCountRef = useRef(laneCount);
	useEffect(() => {
		laneCountRef.current = laneCount;
	}, [laneCount]);

	// Track pending queue size for UI indicator
	useEffect(() => {
		const interval = setInterval(() => {
			setPendingCount(queueRef.current.pending);
		}, 500);
		return () => clearInterval(interval);
	}, []);

	// Send a message to the laptop via relay queue (guaranteed delivery)
	const sendToLaptop = useCallback((payload: DeviceMessage) => {
		const code = sessionCodeRef.current;
		if (!code) return;
		queueRef.current.enqueue(code, "to_laptop", payload);
	}, []);

	// Handle commands from the laptop
	const handleCommand = useCallback(async (command: AppCommand) => {
		const transport = transportRef.current;
		if (!transport) return;
		if (command.type === "arm") {
			setLaneCount(command.lanes);
		}
		await transport.send(command);
	}, []);

	// Poll for commands from the laptop
	const startPolling = useCallback(() => {
		if (pollTimerRef.current) clearInterval(pollTimerRef.current);

		pollTimerRef.current = setInterval(async () => {
			const code = sessionCodeRef.current;
			if (!code || pollingRef.current) return;
			pollingRef.current = true;
			try {
				const res = await fetch(
					`/api/relay?sessionId=${code}&direction=to_phone&after=${lastSeenIdRef.current}`,
				);
				if (!res.ok) return;

				const data = (await res.json()) as {
					messages: Array<{ id: number; payload: AppCommand }>;
				};

				for (const msg of data.messages) {
					lastSeenIdRef.current = Math.max(lastSeenIdRef.current, msg.id);
					handleCommand(msg.payload);
				}
			} catch {
				// Network error — will retry on next poll
			} finally {
				pollingRef.current = false;
			}
		}, POLL_INTERVAL_MS);
	}, [handleCommand]);

	// Start heartbeat — lets laptop know phone is alive
	const startHeartbeat = useCallback(() => {
		if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);

		// Send immediately
		sendToLaptop({ type: "status", state: "idle", lanes: laneCountRef.current, demo: false });

		heartbeatTimerRef.current = setInterval(() => {
			const transport = transportRef.current;
			sendToLaptop({
				type: "status",
				state: transport?.currentState ?? "idle",
				lanes: laneCountRef.current,
				demo: false,
			});
		}, HEARTBEAT_INTERVAL_MS);
	}, [sendToLaptop]);

	// Connect: open camera + start polling + heartbeat
	const connect = useCallback(async () => {
		if (!sessionCodeRef.current) return;

		setError(null);
		try {
			const transport = new CameraTransport();

			// Listen for messages from the transport and relay to laptop
			transport.onMessage((message: DeviceMessage) => {
				// Update local UI state
				if (message.type === "status") {
					setTimerState(message.state);
				} else if (message.type === "result") {
					setLaneResults((prev) => {
						const next = new Map(prev);
						next.set(message.lane, {
							lane: message.lane,
							elapsed_ms:
								message.status === "dnf" ? null : message.elapsed_ms,
							status: message.status,
						});
						return next;
					});
				}

				// Relay ALL messages to laptop via queue
				sendToLaptop(message);
			});

			await transport.connect();
			transportRef.current = transport;
			setStream(transport.stream);
			setRegions(transport.regions);
			setSensitivity(transport.sensitivity);
			setCameraReady(true);
			setConnected(true);

			// Start outbound queue, polling, and heartbeat
			queueRef.current.start(500);
			startPolling();
			startHeartbeat();
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to open camera",
			);
		}
	}, [sendToLaptop, startPolling, startHeartbeat]);

	// Auto-connect if session code came from URL
	const autoConnected = useRef(false);
	useEffect(() => {
		if (initialCode && !autoConnected.current) {
			autoConnected.current = true;
			connect();
		}
	}, [initialCode, connect]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (pollTimerRef.current) clearInterval(pollTimerRef.current);
			if (heartbeatTimerRef.current)
				clearInterval(heartbeatTimerRef.current);
			queueRef.current.stop();
			if (transportRef.current) transportRef.current.disconnect();
		};
	}, []);

	// Sync regions to transport
	useEffect(() => {
		if (transportRef.current && regions.length > 0) {
			transportRef.current.regions = regions;
		}
	}, [regions]);

	// Handle reset of lane results when timer resets
	useEffect(() => {
		if (timerState === "idle" || timerState === "armed") {
			setLaneResults(new Map());
		}
	}, [timerState]);

	// Not connected yet — show code entry
	if (!connected) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center p-4">
				<div className="bg-card rounded-xl p-6 max-w-sm w-full space-y-4">
					<div className="text-center space-y-2">
						<Camera size={48} className="mx-auto text-primary" />
						<h1 className="text-xl font-bold text-foreground">
							Camera Sensor
						</h1>
						<p className="text-sm text-muted-foreground">
							Enter the session code shown on the race control laptop
						</p>
					</div>

					<form
						onSubmit={(e) => {
							e.preventDefault();
							const code = inputCode.toUpperCase().trim();
							setSessionCode(code);
							sessionCodeRef.current = code;
							connect();
						}}
						className="space-y-3"
					>
						<input
							type="text"
							value={inputCode}
							onChange={(e) =>
								setInputCode(e.target.value.toUpperCase())
							}
							placeholder="e.g. A3F7K2"
							maxLength={6}
							className="w-full text-center text-3xl font-mono font-bold tracking-[0.3em] px-4 py-3 rounded-lg bg-secondary text-foreground border-0 outline-none"
							autoFocus
							autoComplete="off"
							autoCapitalize="characters"
						/>
						<button
							type="submit"
							disabled={inputCode.trim().length < 4}
							className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm bg-primary hover:bg-accent text-primary-foreground rounded-lg transition-colors font-bold disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<Camera size={16} />
							Connect Camera
						</button>
					</form>

					{error && (
						<p className="text-destructive text-sm text-center">
							{error}
						</p>
					)}
				</div>
			</div>
		);
	}

	// Connected — show camera + status
	return (
		<div className="min-h-screen bg-background p-3 space-y-3">
			{/* Status bar */}
			<div className="bg-card rounded-xl p-3 flex items-center justify-between">
				<div className="flex items-center gap-2">
					{cameraReady ? (
						<>
							<span className="w-2.5 h-2.5 rounded-full bg-success animate-pulse" />
							<span className="text-foreground text-sm font-bold">
								Connected
							</span>
							<span className="text-xs px-2 py-0.5 rounded bg-secondary text-secondary-foreground font-mono font-bold">
								{sessionCode}
							</span>
						</>
					) : (
						<>
							<WifiOff size={14} className="text-muted-foreground" />
							<span className="text-muted-foreground text-sm font-bold">
								Connecting...
							</span>
						</>
					)}
				</div>
				<div className="flex items-center gap-2">
					{pendingCount > 0 && (
						<span className="text-xs px-2 py-0.5 rounded bg-status-finals text-foreground font-bold tabular-nums">
							{pendingCount} queued
						</span>
					)}
					<TimerStateBadge state={timerState} />
				</div>
			</div>

			{error && (
				<div className="bg-destructive/20 border border-destructive rounded-xl p-3">
					<p className="text-destructive text-sm">{error}</p>
				</div>
			)}

			{/* Camera preview with calibration */}
			{stream && transportRef.current && (
				<CameraPreview
					stream={stream}
					transport={transportRef.current}
					laneRegions={regions}
					onRegionsChange={setRegions}
					laneResults={laneResults}
					sensitivity={sensitivity}
					onSensitivityChange={(v) => {
						setSensitivity(v);
						if (transportRef.current)
							transportRef.current.sensitivity = v;
					}}
					calibrating={calibrating}
					onCalibrateToggle={() => setCalibrating((c) => !c)}
				/>
			)}

			{/* Instructions */}
			<div className="bg-card rounded-xl p-3">
				<p className="text-xs text-muted-foreground text-center">
					{timerState === "idle" &&
						"Waiting for race control to arm the timer..."}
					{timerState === "armed" &&
						"Armed — baseline captured. Waiting for start..."}
					{timerState === "timing" &&
						"Timing — detecting lane crossings..."}
					{timerState === "finished" &&
						"Heat complete. Waiting for reset..."}
					{timerState === "starting" && "Starting..."}
				</p>
			</div>
		</div>
	);
}

function TimerStateBadge({ state }: { state: TimerState }) {
	const config: Record<TimerState, { label: string; className: string }> = {
		idle: {
			label: "Idle",
			className: "bg-secondary text-secondary-foreground",
		},
		armed: {
			label: "Armed",
			className: "bg-status-finals text-foreground",
		},
		starting: {
			label: "Starting",
			className: "bg-status-finals text-foreground",
		},
		timing: {
			label: "Timing",
			className: "bg-primary text-primary-foreground animate-pulse",
		},
		finished: {
			label: "Finished",
			className: "bg-success text-foreground",
		},
	};

	const { label, className } = config[state];
	return (
		<span
			className={`text-xs px-2 py-0.5 rounded font-bold uppercase ${className}`}
		>
			{label}
		</span>
	);
}
